/**
 * First, we will load all of this project's Javascript utilities and other
 * dependencies. Then, we will be ready to develop a robust and powerful
 * application frontend using useful Laravel and JavaScript libraries.
 */

import '@/main.css';
import helpers from '@/bootstrap/helpers';

// Core
import i18n from './bootstrap/i18n';
import layout from '@/main.vue';
import router from '@/bootstrap/router';
import store from '@/bootstrap/vuex';
import VueProgressBar from '@aacassandra/vue3-progressbar';

// import "./bootstrap";
import { createApp } from 'vue';
import '@/bootstrap/axios';

import FormValidator from '@/plugins/form-validator';
import UserSession from '@/plugins/user';
import Money from '@/plugins/money';

import * as Sentry from '@sentry/vue';

import Echarts from 'vue-echarts';

import 'echarts';

import { VueCsvImportPlugin } from 'vue-csv-import';

const options = {
    color: '#8DC63F',
    failedColor: '#874b4b',
    thickness: '4px',
    transition: {
        speed: '2.0s',
        opacity: '0.6s',
        termination: 100,
    },
    autoRevert: true,
    location: 'top',
    inverse: false,
    autoFinish: false,
};

const months = [
    {
        id: 1,
        name: $t('label.month.january'),
        value: 1,
    },
    {
        id: 2,
        name: $t('label.month.february'),
        value: 2,
    },
    {
        id: 3,
        name: $t('label.month.march'),
        value: 3,
    },
    {
        id: 4,
        name: $t('label.month.april'),
        value: 4,
    },
    {
        id: 5,
        name: $t('label.month.may'),
        value: 5,
    },
    {
        id: 6,
        name: $t('label.month.june'),
        value: 6,
    },
    {
        id: 7,
        name: $t('label.month.july'),
        value: 7,
    },
    {
        id: 8,
        name: $t('label.month.august'),
        value: 8,
    },
    {
        id: 9,
        name: $t('label.month.september'),
        value: 9,
    },
    {
        id: 10,
        name: $t('label.month.october'),
        value: 10,
    },
    {
        id: 11,
        name: $t('label.month.november'),
        value: 11,
    },
    {
        id: 12,
        name: $t('label.month.december'),
        value: 12,
    },
];

// check if a user was already logged in and retrieve
store.commit('app/loadUser');

// check if tenant info is set and load
store.commit('organisation/loadOrganisations');

const app = createApp(layout);

Sentry.init({
    app,
    dsn: window.sentryDsn,
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 1.0,

    // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ['localhost', /^https:\/\/headoffice\.app\/api/],

    // Capture Replay for 10% of all sessions,
    // plus for 100% of sessions with an error
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
});

app.config.globalProperties.months = months;
app.config.globalProperties.$helpers = helpers;

// Convenience: `this.$tenantTimezone` returns the active tenant's IANA
// timezone name as a string, for callers that need the raw value (e.g.
// `{ timeZone: $tenantTimezone }` in an Intl options bag). The date helpers
// in `$helpers` (toTenantWallClockDate, toShortDate, toUTC, …) already resolve this
// internally and fail loud on missing — callers should prefer the helpers.
app.mixin({
    computed: {
        $tenantTimezone() {
            const orgId = this.$route?.params?.organisation;
            return this.$store?.state?.organisation?.[orgId]?.settings?.timezone ?? null;
        },
    },
});

app.use(UserSession);
app.use(i18n);
app.use(router);
app.use(store);
app.use(FormValidator);
app.use(Money);
app.use(VueProgressBar, options);
app.use(VueCsvImportPlugin);

app.component('v-chart', Echarts);

app.mount('#js-app');

window.Vue = app;

export default app;
